import React from "react";
import ReactDOM from "react-dom/client";
import { formatNumber } from "../../../../app/formatters/NumberFormatter";
// NOTE Seule la logique pure de simulation est importée : CfcFeeLogic lit les options côté serveur et ne doit pas être chargée dans le navigateur
import { simulateFees } from "../../logics/CfcFeeSimulationLogic.js";
import { SPORT_TYPE } from "../../enums.js";

/** @typedef {import('../../logics/CfcFeeLogic.js').CfcFeeRatesObj} CfcFeeRatesObj */
/** @typedef {import('../../logics/CfcFeeSimulationLogic.js').CfcFeeSimulationObj} CfcFeeSimulationObj */

/**
 * @typedef {object} CfcFeeSimulatorProps
 * @property {Array<CfcFeeRatesObj>} feeRatesList - Taux des frais par sport (un seul sport pour une compétition : pas de choix affiché)
 * @property {?string} initialSportType - Sport sélectionné au départ
 * @property {boolean} horizontal - Affichage sur deux colonnes (saisie à gauche, revenus estimés à droite)
 * @property {boolean} syncWithForm - Si les champs « Prix TTC » et « Nombre d'inscription » de la page pré-remplissent le simulateur
 * @property {string} registrationsInputId - Identifiant du champ « Nombre d'inscription » de la page (max_registrations pour une distance, number pour une catégorie)
 * @property {string} idPrefix - Préfixe des identifiants des champs, unique par simulateur de la page
 * @property {boolean} chronoAvailable - Si le choix du chronométrage est proposé (compétition chronométrée sur les pages d'une compétition, toujours pour le shortcode)
 * @property {boolean} initialWithChrono - Chronométrage sélectionné au départ
 */

/**
 * @typedef {object} CfcFeeSimulatorState
 * @property {number} price - Prix d'une inscription en euros
 * @property {number} nbRegistrations - Nombre d'inscriptions
 * @property {boolean} withChrono - Si la compétition est chronométrée
 * @property {?string} sportType - Sport sélectionné
 */

// NOTE Prix minimum d'une distance et bornes des curseurs (étendues si l'organisateur saisit plus)
const MIN_PRICE = 10;
const DEFAULT_PRICE = 25;
const DEFAULT_NB_REGISTRATIONS = 100;
const MAX_PRICE_RANGE = 1000;
const MAX_REGISTRATIONS_RANGE = 5000;

class CfcFeeSimulator extends React.Component {
  /**
   * @param {CfcFeeSimulatorProps} props
   */
  constructor(props) {
    super(props);

    this.state = /** @type {CfcFeeSimulatorState} */ ({
      price: DEFAULT_PRICE,
      nbRegistrations: DEFAULT_NB_REGISTRATIONS,
      withChrono: props.chronoAvailable && props.initialWithChrono,
      sportType: props.initialSportType || props.feeRatesList[0]?.sportType || null
    });
  }

  componentDidMount() {
    if (!this.props.syncWithForm) {
      return;
    }

    // NOTE Les champs « Prix TTC » et « Nombre d'inscription » du formulaire (distance ou catégorie) pré-remplissent le simulateur,
    // dès l'affichage pour une modification (valeurs déjà enregistrées) puis à chaque saisie
    this.priceInput = /** @type {?HTMLInputElement} */ (document.getElementById("price"));
    this.maxRegistrationsInput = /** @type {?HTMLInputElement} */ (document.getElementById(this.props.registrationsInputId));
    // NOTE Select « Chronométrage » d'une distance, présent uniquement si la compétition est chronométrée
    this.chronoSelect = /** @type {?HTMLSelectElement} */ (document.querySelector("select#chronometrage"));
    this.priceInput?.addEventListener("input", this.onFormPriceChange);
    this.maxRegistrationsInput?.addEventListener("input", this.onFormMaxRegistrationsChange);
    this.chronoSelect?.addEventListener("change", this.onFormChronoChange);
    this.setPriceFromForm();
    this.setRegistrationsFromForm();
    this.setChronoFromForm();
  }

  componentWillUnmount() {
    this.priceInput?.removeEventListener("input", this.onFormPriceChange);
    this.maxRegistrationsInput?.removeEventListener("input", this.onFormMaxRegistrationsChange);
    this.chronoSelect?.removeEventListener("change", this.onFormChronoChange);
  }

  /**
   * Reprise du chronométrage sélectionné dans le formulaire d'une distance
   */
  setChronoFromForm() {
    if (this.chronoSelect && this.props.chronoAvailable) {
      this.setState({ withChrono: this.chronoSelect.value === "1" });
    }
  }

  onFormChronoChange = () => this.setChronoFromForm();

  /**
   * Choix du chronométrage dans le simulateur, reporté dans le select du formulaire d'une distance
   * @param {boolean} withChrono - Si la compétition est chronométrée
   */
  onChangeChrono(withChrono) {
    this.setState({ withChrono });

    if (this.chronoSelect && this.chronoSelect.value !== (withChrono ? "1" : "0")) {
      this.chronoSelect.value = withChrono ? "1" : "0";
      this.chronoSelect.dispatchEvent(new Event("change", { bubbles: true }));
    }
  }

  /**
   * Reprise du prix saisi dans le formulaire (ignoré s'il est vide)
   */
  setPriceFromForm() {
    const price = parseFloat(this.priceInput?.value || "");

    if (!Number.isNaN(price)) {
      this.setState({ price: Math.max(price, 0) });
    }
  }

  /**
   * Reprise du nombre d'inscriptions saisi dans le formulaire (ignoré s'il est vide)
   */
  setRegistrationsFromForm() {
    const nbRegistrations = parseInt(this.maxRegistrationsInput?.value || "", 10);

    if (!Number.isNaN(nbRegistrations)) {
      this.setState({ nbRegistrations: Math.max(nbRegistrations, 0) });
    }
  }

  onFormPriceChange = () => this.setPriceFromForm();

  onFormMaxRegistrationsChange = () => this.setRegistrationsFromForm();

  /**
   * Modification d'une valeur numérique (saisie, curseur ou compteur)
   * @param {'price'|'nbRegistrations'} fieldName - Valeur modifiée
   * @param {string|number} value - Nouvelle valeur
   */
  onChangeValue(fieldName, value) {
    const number = fieldName === "price" ? parseFloat(String(value)) : parseInt(String(value), 10);

    this.setState(/** @type {any} */ ({ [fieldName]: Number.isNaN(number) ? 0 : Math.max(number, 0) }));
  }

  /**
   * Taux des frais du sport sélectionné
   * @returns {CfcFeeRatesObj}
   */
  getFeeRates() {
    const { feeRatesList } = this.props;

    return feeRatesList.find(feeRates => feeRates.sportType === this.state.sportType) || feeRatesList[0];
  }

  /**
   * Formate un montant en euros
   * @param {number} amount - Montant
   * @returns {string}
   */
  formatEuro(amount) {
    return formatNumber(String(amount), "€", 2);
  }

  /**
   * Décrit le taux d'un frais par transaction (ex : « 1.50 % + 0.25 € »)
   * @param {number} percent - Pourcentage
   * @param {number} euro - Montant fixe
   * @returns {string}
   */
  formatRate(percent, euro) {
    const parts = [];

    if (percent) {
      parts.push(formatNumber(String(percent), "%", 2));
    }

    if (euro) {
      parts.push(this.formatEuro(euro));
    }

    return parts.join(" + ") || "0 %";
  }

  /**
   * Explication d'un frais affichée sous son libellé
   * @param {number} index - Position du frais (paiement en ligne, plateforme, chronométrage)
   * @param {CfcFeeSimulationObj} simulation - Résultat de la simulation
   * @returns {string}
   */
  getFeeHelp(index, simulation) {
    const helps = ["Prélevés sur chaque paiement par carte bancaire", `Selon le sport de la compétition${this.getFeeRates()?.sportType ? ` (${this.getFeeRates().sportType})` : ""}, appliqués au montant restant après les frais de paiement en ligne (${this.formatEuro(simulation.netPerAthlete)})`, "Ajoutés à chaque inscription d'une compétition chronométrée"];

    return helps[index] || "";
  }

  /**
   * Affiche un compteur avec saisie directe, boutons −/+ et curseur
   * @param {string} id - Identifiant du champ
   * @param {string} label - Libellé
   * @param {'price'|'nbRegistrations'} fieldName - Valeur modifiée
   * @param {number} step - Pas des boutons −/+
   * @param {number} min - Valeur minimum du curseur
   * @param {number} maxRange - Valeur maximum du curseur
   * @param {string} unit - Unité affichée à côté du libellé
   * @returns {React.ReactNode}
   */
  renderCounter(id, label, fieldName, step, min, maxRange, unit) {
    const value = this.state[fieldName];

    return (
      <div className="flexline flexline--no-mobile flexline--gap-8 flexline--direction-column">
        <div className="flexline flexline--no-mobile flexline--spaced flexline--16 flexline--align-vz">
          <label htmlFor={id} className="paragraph paragraph--no-margin paragraph--small-font-size paragraph--uppercase">
            {label} <strong>({unit})</strong>
          </label>
          <div className="buttongroup cfcfeesimulator__counter">
            <button type="button" className="buttongroup__button" onClick={() => this.onChangeValue(fieldName, value - step)} aria-label={`Diminuer - ${label}`}>
              <span className="material-icons">remove</span>
            </button>
            <input id={id} className="cfcfeesimulator__counter-input" type="number" min="0" step={fieldName === "price" ? ".01" : "1"} value={value} onChange={event => this.onChangeValue(fieldName, event.target.value)} />
            <button type="button" className="buttongroup__button" onClick={() => this.onChangeValue(fieldName, value + step)} aria-label={`Augmenter - ${label}`}>
              <span className="material-icons">add</span>
            </button>
          </div>
        </div>
        <input className="cfcfeesimulator__range" type="range" min={min} max={Math.max(maxRange, value)} step="1" value={value} aria-label={label} onChange={event => this.onChangeValue(fieldName, event.target.value)} />
      </div>
    );
  }

  /**
   * Affiche la barre de répartition entre les revenus et les frais
   * @param {CfcFeeSimulationObj} simulation - Résultat de la simulation
   * @returns {React.ReactNode}
   */
  renderBar(simulation) {
    const feesShare = simulation.pricePerAthlete ? Math.min((simulation.feesPerAthlete / simulation.pricePerAthlete) * 100, 100) : 0;
    const revenueShare = simulation.pricePerAthlete ? 100 - feesShare : 0;

    return (
      <div className="flexline flexline--no-mobile flexline--gap-8 flexline--direction-column">
        <div className="cfcfeesimulator__bar" role="img" aria-label={`Revenus ${revenueShare.toFixed(1)} %, frais ${feesShare.toFixed(1)} %`}>
          <div className="cfcfeesimulator__bar-part cfcfeesimulator__bar-part--revenue" style={{ width: `${revenueShare}%` }}></div>
          <div className="cfcfeesimulator__bar-part cfcfeesimulator__bar-part--fees" style={{ width: `${feesShare}%` }}></div>
        </div>
        <div className="flexline flexline--no-mobile flexline--wrap flexline--spaced flexline--gap-8 paragraph paragraph--small-font-size">
          <span>Vos revenus : {formatNumber(String(revenueShare), "%", 1)}</span>
          <span>Frais : {formatNumber(String(feesShare), "%", 1)}</span>
        </div>
      </div>
    );
  }

  render() {
    const { feeRatesList, horizontal, idPrefix } = this.props;
    const { price, nbRegistrations, withChrono, sportType } = this.state;
    const feeRates = this.getFeeRates();
    const simulation = simulateFees(feeRates, price, nbRegistrations, withChrono);

    return (
      <div className={`cfcfeesimulator${horizontal ? " cfcfeesimulator--horizontal" : ""}`}>
        <div className="flexline flexline--no-mobile flexline--gap-24 flexline--direction-column padding--24">
          <h3 className="title title--h4">Simulateur de vos frais</h3>
          {feeRatesList.length > 1 ? (
            <div className="flexline flexline--no-mobile flexline--gap-8 flexline--direction-column">
              <p className="paragraph paragraph--no-margin paragraph--small-font-size paragraph--uppercase">Sport de la compétition</p>
              <div className="flexline flexline--gap-8 flexline--no-mobile">
                {feeRatesList.map(sportFeeRates => (
                  <button type="button" key={sportFeeRates.sportType} className={`btn ${sportFeeRates.sportType === sportType ? "btn--main-color" : "btn--outline-secondary-color"}`} onClick={() => this.setState({ sportType: sportFeeRates.sportType })}>
                    {sportFeeRates.sportType}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            ""
          )}
          {this.renderCounter(`${idPrefix}-price`, "Prix d'une inscription", "price", 1, MIN_PRICE, MAX_PRICE_RANGE, "€")}
          {this.renderCounter(`${idPrefix}-registrations`, "Nombre d'inscriptions", "nbRegistrations", 1, 1, MAX_REGISTRATIONS_RANGE, "athlètes")}
          {this.props.chronoAvailable ? (
            <div className="flexline flexline--no-mobile flexline--gap-8 flexline--direction-column">
              <span className="paragraph paragraph--no-margin paragraph--small-font-size paragraph--uppercase">Compétition chronométrée</span>
              <div className="flexline flexline--gap-8 flexline--no-mobile">
                {[false, true].map(value => (
                  <button type="button" key={String(value)} className={`btn ${withChrono === value ? "btn--main-color" : "btn--outline-secondary-color"}`} onClick={() => this.onChangeChrono(value)}>
                    {value ? `Oui (+ ${this.formatEuro(feeRates.chronoEuro)} par inscription)` : "Non"}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            ""
          )}
        </div>

        <div className="flexline flexline--no-mobile flexline--direction-column flexline--gap-16 padding--24 background--main-color">
          <p className="paragraph paragraph--no-margin paragraph--small-font-size paragraph--uppercase paragraph--secondary-color paragraph--bold-700">Vos revenus estimés</p>
          <span className="cfcfeesimulator__price">{this.formatEuro(simulation.totalRevenue)}</span>
          <span className="cfcfeesimulator__detail">
            soit {this.formatEuro(simulation.revenuePerAthlete)} par athlète, pour {nbRegistrations} inscription(s) à {this.formatEuro(simulation.pricePerAthlete)}
          </span>
          {this.renderBar(simulation)}
          <div className="grid grid--has-2-child grid--8-gap">
            <div className="cfcfeesimulator__total">
              <span className="cfcfeesimulator__detail">Encaissé au total</span>
              <span className="cfcfeesimulator__total-value">{this.formatEuro(simulation.totalAmount)}</span>
            </div>
            <div className="cfcfeesimulator__total">
              <span className="cfcfeesimulator__detail">Frais au total</span>
              <span className="cfcfeesimulator__total-value">{this.formatEuro(simulation.totalFees)}</span>
            </div>
            <div className="cfcfeesimulator__total">
              <span className="cfcfeesimulator__detail">Revenu par athlète</span>
              <span className="cfcfeesimulator__total-value">{this.formatEuro(simulation.revenuePerAthlete)}</span>
            </div>
            <div className="cfcfeesimulator__total">
              <span className="cfcfeesimulator__detail">Frais par athlète</span>
              <span className="cfcfeesimulator__total-value">{this.formatEuro(simulation.feesPerAthlete)}</span>
            </div>
          </div>
          <div className="cfcfeesimulator__fees">
            <span className="paragraph paragraph--no-margin paragraph--small-font-size paragraph--uppercase">Frais par inscription</span>
            {simulation.details.map((detail, index) => (
              <div className="flexline flexline--no-mobile flexline--align-vz flexline--spaced flexline--gap-8" key={detail.label}>
                <div className="flexline flexline--no-mobile flexline--direction-column">
                  <span>{detail.label}</span>
                  <span className="cfcfeesimulator__fee-help">{this.getFeeHelp(index, simulation)}</span>
                </div>
                <span className="cfcfeesimulator__highlight">{this.formatRate(detail.percent, detail.euro)}</span>
                <strong>{this.formatEuro(detail.amountPerAthlete)}</strong>
              </div>
            ))}
            <div className="flexline flexline--no-mobile flexline--align-vz flexline--spaced flexline--gap-8 cfcfeesimulator__fee--total">
              <span>Total par inscription</span>
              <span>{this.formatEuro(simulation.feesPerAthlete)}</span>
            </div>
          </div>
          <span className="cfcfeesimulator__detail">Les frais de paiement en ligne s'appliquent au prix de chaque inscription, puis les frais de la plateforme au montant restant. Les frais sont déduits du prix payé par l'athlète.</span>
        </div>
      </div>
    );
  }
}

/**
 * Récupère les taux de frais du simulateur :
 * - data-feerates : taux d'une compétition calculés par le serveur (ajout et modification d'une distance ou d'une catégorie), un seul sport ;
 * - sinon (shortcode [cfcfeesimulator]) : taux de chaque sport renvoyés par l'API, limités au sport de data-sporttype s'il est précisé
 * @param {HTMLElement} node - Nœud de montage
 * @returns {Promise<Array<CfcFeeRatesObj>>}
 */
async function getFeeRatesListFromNode(node) {
  if (node.dataset.feerates) {
    return [JSON.parse(node.dataset.feerates)];
  }

  const response = await fetch("/api/cfc-tournament/feerates", { headers: { Authorization: "gestiaweb" } });
  const feeRatesBySport = /** @type {Array<CfcFeeRatesObj>} */ ((await response.json()).item || []);
  // NOTE Le paramètre sporttype du shortcode reprend les clés de SPORT_TYPE en minuscules (ex : hybrid_race)
  const sportType = node.dataset.sporttype ? SPORT_TYPE[node.dataset.sporttype.toUpperCase()] : null;
  const sportFeeRates = sportType ? feeRatesBySport.filter(feeRates => feeRates.sportType === sportType) : [];

  return sportFeeRates.length ? sportFeeRates : feeRatesBySport;
}

// NOTE Pages d'ajout et de modification d'une distance ou d'une catégorie (#cfc-simu-fees) et shortcode [cfcfeesimulator] (.cfc-fee-simulator), plusieurs simulateurs possibles sur une page
document.querySelectorAll("#cfc-simu-fees, .cfc-fee-simulator").forEach(async (cfcFeeSimulatorNode, index) => {
  const node = /** @type {HTMLElement} */ (cfcFeeSimulatorNode);
  const feeRatesList = await getFeeRatesListFromNode(node);

  if (!feeRatesList.length) {
    return;
  }

  ReactDOM.createRoot(node).render(<CfcFeeSimulator feeRatesList={feeRatesList} initialSportType={null} horizontal={node.dataset.mode === "horizontal"} syncWithForm={node.id === "cfc-simu-fees"} registrationsInputId={node.dataset.registrationsinput || "max_registrations"} idPrefix={`cfc-fee-simulator-${index}`} chronoAvailable={node.dataset.chronometrage !== "0"} initialWithChrono={node.dataset.chronometrage === "1"} />);
});
